import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, type User } from '../lib/api'

interface AuthCtx {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  updateUser: (data: { name?: string; email?: string }) => Promise<void>
  refreshUser: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // No localStorage — session state is carried by the HttpOnly cookie.
    // Call /me so the browser sends the cookie and we get the user back.
    api.auth.me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  async function login(email: string, password: string) {
    // The API sets an HttpOnly cookie in the response — no token handling needed here.
    const { user } = await api.auth.login({ email, password })
    setUser(user)
  }

  async function register(name: string, email: string, password: string) {
    const { user } = await api.auth.register({ email, password, name })
    setUser(user)
  }

  async function logout() {
    // The API clears the cookie server-side via Set-Cookie: Max-Age=0.
    await api.auth.logout().catch(() => {})
    setUser(null)
  }

  async function updateUser(data: { name?: string; email?: string }) {
    const updated = await api.user.updateProfile(data)
    setUser(updated)
  }

  async function refreshUser() {
    const fresh = await api.auth.me()
    setUser(fresh)
  }

  return <Ctx.Provider value={{ user, loading, login, register, logout, updateUser, refreshUser }}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
